import { onBackClickCallback, onNextClickType } from 'components/types'
import React, { useState } from 'react'
import styled, { keyframes } from 'styled-components'
import { useForm, FormProvider, Controller } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as Yup from 'yup'
import { ContextAwareText } from '../core/text/ContextAwareText'
import { DSButton, DSCheckbox, DSFlex, DSInput, DSSpacer, DSText } from '@zoe/ds-web'
import { StickyFooter, StickyFooterContent } from '../core/layout/StickyFooter'
import { ContentContainer } from '../core/styled/ContentContainer'
import { useFocusOnMount } from '../../hooks/useFocusOnMount'
import { GifName, resolveTopImage } from '../../data/gifs'

const Main = styled.main`
  display: flex;
  flex-direction: row;
  flex: 1;
  width: 100%;
  justify-content: center;
  background-color: var(--colour-surface-white);
`

const EmailContentContainer = styled(ContentContainer)`
  display: flex;
  flex-direction: column;
`

const imgFadeIn = keyframes`
  from { opacity: 0; transform: scale(0.97); }
  to { opacity: 1; transform: scale(1); }
`

const TopImage = styled.img.attrs({ loading: 'lazy' })`
  display: block;
  margin: 0 auto;
  width: 100%;
  object-fit: cover;
  border-radius: var(--radius-12);
  margin-bottom: var(--grid-24);
  animation: ${imgFadeIn} 0.3s ease-out both;
`

const OptOutLink = styled.a`
  cursor: pointer;
  color: var(--colour-text-link);
`

const OptInRadioContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding: 16px;
  border-radius: 8px;
  background-color: #faf8f5;

  label {
    cursor: pointer;
    user-select: none;
  }
`

const OptInRadioGroup = styled.div`
  display: flex;
  gap: 20px;
  align-items: center;
  margin-top: 8px;
`

const OptInRadioLabel = styled.label`
  color: var(--colour-text-primary);
  display: flex;
  align-items: center;
  cursor: pointer;
  font-weight: 300;
`

const OptInRadioInput = styled.input`
  appearance: none;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  margin-right: 8px;
  cursor: pointer;
  outline: 1px solid #ccc;
  background-color: #fff;

  &:checked {
    background-color: #2d3245;
    border: 5px solid #fff;
  }

  &:focus {
    outline: 2px solid #ccc;
  }
`

export type EmailCaptureScreenProps = {
  title: string
  description?: string
  buttonText: string
  optInCopy?: string
  footerBody: string
  crmTag?: string
  topImage?: string
  topImageAlt?: string
  topImageAspectRatio?: string
  gif?: GifName
  showMarketingConsent?: boolean
  defaultMarketingOptIn?: boolean
  copyright?: string
}

type Props = EmailCaptureScreenProps & {
  onNextClick: onNextClickType
  onBackClick?: onBackClickCallback
}

const validationSchema = Yup.object({
  email: Yup.string().email('Please enter a valid email address').required('Please enter a valid email address'),
  marketingOptIn: Yup.bool().required(),
})

export const EmailCaptureScreen: React.FC<Props> = ({
  title,
  optInCopy,
  topImage,
  topImageAlt,
  topImageAspectRatio,
  gif,
  description,
  buttonText,
  footerBody,
  onNextClick,
  crmTag,
  copyright,
  showMarketingConsent = true,
  defaultMarketingOptIn,
}) => {
  const image = resolveTopImage(gif, { topImage, topImageAlt, topImageAspectRatio })
  const [showOptOutBox, setShowOptOutBox] = useState(false)
  const mainRef = useFocusOnMount<HTMLElement>()

  const formId = React.useId()
  const methods = useForm({
    defaultValues: {
      email: '',
      marketingOptIn: defaultMarketingOptIn,
      ...(crmTag && { crmTag }),
    },
    resolver: yupResolver(validationSchema),
    mode: 'onTouched',
  })

  const {
    handleSubmit,
    formState: { isSubmitting, isValid, isDirty, errors },
  } = methods

  const onSubmit = (values: { email: string; marketingOptIn: boolean; crmTag?: string; marketingOptOut?: boolean }) => {
    onNextClick({ ...values, ...(crmTag && { crmTag }) })
  }

  const handleOptOutClick = () => {
    setShowOptOutBox(!showOptOutBox)
  }
  const optOutLink = (
    <OptOutLink onClick={handleOptOutClick} style={showOptOutBox ? {} : {}}>
      Opt out
    </OptOutLink>
  )

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {/* Back navigation is handled by the header bar */}
      </div>
      <Main ref={mainRef} tabIndex={-1} style={{ outline: 'none' }}>
        <EmailContentContainer>
          <DSText variant="fluid-heading-600" weight="semiBold" align="left">
            {title}
          </DSText>
          {image.topImage && (
            <>
              <DSSpacer size={16} direction="vertical" />
              <TopImage
                src={image.topImage}
                alt={image.topImageAlt}
                style={image.topImageAspectRatio ? { aspectRatio: image.topImageAspectRatio } : undefined}
              />
            </>
          )}
          {description && (
            <>
              <DSSpacer size={16} direction="vertical" />
              <DSText variant="fluid-paragraph-200" color="secondary" weight="light">
                {description}
              </DSText>
            </>
          )}
          <br />
          <FormProvider {...methods}>
            <form id={formId} onSubmit={handleSubmit(onSubmit)}>
              <Controller
                name="email"
                render={({ field }) => (
                  <DSInput
                    id="email"
                    label=""
                    name={field.name}
                    value={field.value ?? ''}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    type="email"
                    placeholder="Your email address"
                    testId="email-input"
                    isInvalid={!!errors.email}
                    errorMessage={errors.email?.message}
                  />
                )}
              />
              {optInCopy && (
                <OptInRadioContainer>
                  <label htmlFor="marketingOptIn" style={{ fontWeight: 300 }}>
                    {optInCopy}
                  </label>

                  {showMarketingConsent && (
                    <OptInRadioGroup role="group">
                      <OptInRadioLabel>
                        <Controller
                          name="marketingOptIn"
                          render={({ field }) => (
                            <OptInRadioInput
                              type="radio"
                              value="true"
                              checked={field.value === true}
                              onChange={() => field.onChange(true)}
                            />
                          )}
                        />
                        Yes
                      </OptInRadioLabel>
                      <OptInRadioLabel>
                        <Controller
                          name="marketingOptIn"
                          render={({ field }) => (
                            <OptInRadioInput
                              type="radio"
                              value="false"
                              checked={field.value === false}
                              onChange={() => field.onChange(false)}
                            />
                          )}
                        />
                        No
                      </OptInRadioLabel>
                    </OptInRadioGroup>
                  )}
                </OptInRadioContainer>
              )}
              <StickyFooter $expanded={showOptOutBox}>
                <StickyFooterContent>
                  {showOptOutBox && (
                    <>
                      <DSFlex direction="row" justifyContent="flex-start">
                        <Controller
                          name="marketingOptOut"
                          render={({ field }) => (
                            <DSCheckbox
                              isSelected={!!field.value}
                              onChange={field.onChange}
                              label="I don't want ongoing emails"
                              testId="marketing-opt-out"
                            />
                          )}
                        />
                      </DSFlex>
                      <DSSpacer size={12} direction="vertical" />
                    </>
                  )}
                  <DSButton
                    variant="primary"
                    colorScheme="default"
                    size="large"
                    width="full"
                    type="submit"
                    /* StickyFooter portals out of the <form>; associate explicitly. */
                    form={formId}
                    isDisabled={!(isValid && isDirty) || isSubmitting}
                    testId="continue-button"
                  >
                    <DSText variant="fluid-paragraph-200" weight="semiBold">
                      {buttonText}
                    </DSText>
                  </DSButton>
                  <DSSpacer size={12} direction="vertical" />
                  <DSText variant="fluid-label-100" color="secondary" align="center" weight="light">
                    <ContextAwareText text={footerBody} additionalTokens={{ optOutLink }} />
                  </DSText>
                </StickyFooterContent>
              </StickyFooter>
            </form>
          </FormProvider>
        </EmailContentContainer>
      </Main>

      {copyright && (
        <footer style={{ textAlign: 'center', padding: 'var(--grid-16) 0' }}>
          <DSText variant="fluid-label-100">{copyright}</DSText>
        </footer>
      )}
    </>
  )
}
